import React, {useCallback, useMemo, useState} from "react";
import LoadingLottieAnimation from "../../../../../../../components/app-specific/lottie-animations/loading";
import useAccessLevels from "../../../../../../../hooks/use-access-levels";
import ImportCsvFile, {ImportCsvFileSteps} from "../../../../../../../components/app-specific/import-csv-file";
import {Alert, Box, IconButton, Typography} from "@mui/material";
import {Link} from "react-router-dom";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {routes} from "../../../../../../../../core/models/static/routes/app-routes";
import {DEFAULT_PRODUCT_CODE_SPLITTER, ProductCodeSplitterControlled,} from "../../../../../../../components/app-specific/product-code-splitter";
import {initialImportDataViewState} from "../../index";
import {DimensionMetricController, useSelectedDimensionMetrics} from "../../../../../../../components/app-specific/manage-product-dimensions/unit-management";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {useTranslation} from "react-i18next";
import {ImportingDataTypes, ImportingSteps} from "../../types";
import {isIndustryScopedImportDataSource, resolveImportIndustryId,} from "../../engine/shared/import-industry-context";
import {industryHasUnsupportedMultiselectPredefined} from "../../engine/shared/technical-import-helpers";
import ImportIndustryController from "../../components/import-industry-controller";
import {buildDataSourceOptions, buildImportFields, filterDataSourceOptionsByAccess, resolveCatalogEntry,} from "../../catalog";
import DataSourceCatalog from "./data-source-catalog";
import SelectedDataSourceSummary from "./selected-data-source-summary";

const SelectImportDataSourceSection = ({
                                           state,
                                           setState,
                                           onMappingComplete,
                                           loading,
                                           allPriceLevels,
                                           allIndustries,
                                           allWarehouses,
                                           technicalPropertiesOfIndustry,
                                           allPartCodeCategories,
                                       }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.settingsImport'});
    const {t: tCatalog} = useTranslation('views', {keyPrefix: 'app-specific.settingsImport.dataSourceCatalog'});
    const operatorAccessLevels = useAccessLevels();
    const {lengthUnit, weightUnit} = useSelectedDimensionMetrics();

    const [selectedDataSource, setSelectedDataSources] = useState(state.selectedDataType);
    const [transformingData, setTransformingData] = useState(false);
    const isMounted = useIsMounted();

    React.useEffect(() => {
        if (!selectedDataSource?.id || !isIndustryScopedImportDataSource(selectedDataSource.id)) {
            return;
        }
        if (state.importIndustryId != null && state.importIndustryId !== '') {
            return;
        }
        if ((allIndustries?.length ?? 0) === 1) {
            setState((prev) => ({
                ...prev,
                importIndustryId: allIndustries[0]?.id ?? null,
            }));
        }
    }, [selectedDataSource?.id, allIndustries, state.importIndustryId, setState]);

    const resolvedImportIndustryId = useMemo(
        () => resolveImportIndustryId(selectedDataSource?.id, state.importIndustryId),
        [selectedDataSource?.id, state.importIndustryId],
    );

    const requiresIndustrySelection = useMemo(
        () => isIndustryScopedImportDataSource(selectedDataSource?.id) && (allIndustries?.length ?? 0) > 1,
        [selectedDataSource?.id, allIndustries?.length],
    );

    const hasRequiredIndustry = useMemo(() => {
        if (!isIndustryScopedImportDataSource(selectedDataSource?.id)) {
            return true;
        }
        return Boolean(resolvedImportIndustryId);
    }, [selectedDataSource?.id, resolvedImportIndustryId]);

    const dataSourcesOptions = useMemo(() => {
        const allOptions = buildDataSourceOptions(allIndustries);
        return filterDataSourceOptionsByAccess(allOptions, operatorAccessLevels);
    }, [allIndustries, operatorAccessLevels]);

    const catalogContext = useMemo(() => ({
        allPriceLevels,
        lengthUnit,
        weightUnit,
        allIndustries,
        allWarehouses,
        technicalPropertiesOfIndustry,
        allPartCodeCategories,
        codeImportSplitter: state.codeImportSplitter ?? DEFAULT_PRODUCT_CODE_SPLITTER,
        serialImportSplitter: state.serialImportSplitter ?? ',',
        industryId: resolvedImportIndustryId,
    }), [
        allPriceLevels,
        lengthUnit,
        weightUnit,
        allIndustries,
        allWarehouses,
        technicalPropertiesOfIndustry,
        allPartCodeCategories,
        state.codeImportSplitter,
        state.serialImportSplitter,
        resolvedImportIndustryId,
    ]);

    const fields = useMemo(
        () => buildImportFields(selectedDataSource?.id, catalogContext),
        [selectedDataSource?.id, catalogContext],
    );

    const selectedCatalogEntry = useMemo(() => {
        if (!selectedDataSource) {
            return null;
        }
        return resolveCatalogEntry(selectedDataSource, catalogContext, tCatalog, {
            noCodeGroups: selectedDataSource.id === ImportingDataTypes.productCodeAssignment
                && !(allPartCodeCategories?.length),
        });
    }, [selectedDataSource, catalogContext, tCatalog, allPartCodeCategories?.length]);

    const sampleFileRowCount = useMemo(() => {
        switch (selectedDataSource?.id) {
            case ImportingDataTypes.warehouseAlertThresholds:
                return Math.max((allWarehouses?.length ?? 0) * 2, 10);
            default:
                return undefined;
        }
    }, [selectedDataSource?.id, allWarehouses]);

    const showMultiselectTechnicalImportWarning = useMemo(() => {
        if (selectedDataSource?.id !== ImportingDataTypes.partTechnicalInformation) {
            return false;
        }
        return industryHasUnsupportedMultiselectPredefined(
            technicalPropertiesOfIndustry[resolvedImportIndustryId] ?? [],
        );
    }, [selectedDataSource?.id, resolvedImportIndustryId, technicalPropertiesOfIndustry]);

    const resolveInitialIndustryId = useCallback((dataSource, currentIndustryId) => {
        if (!isIndustryScopedImportDataSource(dataSource?.id)) {
            return null;
        }
        if (currentIndustryId != null && currentIndustryId !== '') {
            return currentIndustryId;
        }
        if ((allIndustries?.length ?? 0) === 1) {
            return allIndustries[0]?.id ?? null;
        }
        return null;
    }, [allIndustries]);

    const onDataSourceChanged = useCallback((e, val) => {
        setSelectedDataSources(val);
        setState({
            ...initialImportDataViewState,
            selectedDataType: val,
            importIndustryId: resolveInitialIndustryId(val, state.importIndustryId),
        });
    }, [setState, resolveInitialIndustryId, state.importIndustryId]);

    const onChangeDataset = useCallback(() => {
        onDataSourceChanged(null, null);
    }, [onDataSourceChanged]);

    const onImportIndustryChange = useCallback((industryId) => {
        setState((prevState) => ({
            ...prevState,
            importIndustryId: industryId,
            parsedRows: null,
            parsedColumns: null,
            columnMapping: null,
        }));
    }, [setState]);

    const onCodeImportSplitterChange = useCallback((splitter) => {
        setState((prevState) => ({
            ...prevState,
            codeImportSplitter: splitter,
        }));
    }, [setState]);

    const onSerialImportSplitterChange = useCallback((splitter) => {
        setState((prevState) => ({
            ...prevState,
            serialImportSplitter: splitter,
        }));
    }, [setState]);

    const isProductCodeAssignmentWithoutGroups =
        selectedDataSource?.id === ImportingDataTypes.productCodeAssignment
        && !(allPartCodeCategories?.length);

    const onDropzoneParsed = useCallback(({rows, columns}) => {
        setState(prevState => ({
            ...prevState,
            parsedRows: rows,
            parsedColumns: columns,
            step: ImportingSteps.mapColumns,
        }));
    }, [setState]);

    const onColumnMapperParsed = useCallback(async (parsedRows, columnMapping) => {
        setTransformingData(true);
        await onMappingComplete({parsedRows, columnMapping, fields});
        if (!isMounted()) {
            return;
        }
        setTransformingData(false);
    }, [onMappingComplete, fields, isMounted]);

    const dropzoneNotes = useMemo(() => (
        <div className="import-notes">
            <p className={'placeholder-text'}>{tCatalog('dropzone.requiredColumnsNote')}</p>
            <p className={'placeholder-text'}>{tCatalog('dropzone.sampleFileNote')}</p>
            {selectedDataSource?.id === ImportingDataTypes.productDimensions && (
                <Box
                    sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', marginBlockStart: 2}}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                >
                    <DimensionMetricController/>
                </Box>
            )}
            {selectedDataSource?.id === ImportingDataTypes.productCodeAssignment && (
                <Box
                    sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', marginBlockStart: 2}}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                >
                    <ProductCodeSplitterControlled
                        value={state.codeImportSplitter}
                        onChange={onCodeImportSplitterChange}
                        column
                        description={tCatalog('dropzone.codeSplitterDescription')}
                    />
                </Box>
            )}
            {selectedDataSource?.id === ImportingDataTypes.setInventoryAvailability && (
                <Box
                    sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', marginBlockStart: 2}}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                >
                    <ProductCodeSplitterControlled
                        value={state.serialImportSplitter}
                        onChange={onSerialImportSplitterChange}
                        column
                        description={tCatalog('dropzone.serialSplitterDescription')}
                        label={tCatalog('dropzone.serialSplitterLabel')}
                    />
                </Box>
            )}
        </div>
    ), [
        selectedDataSource?.id,
        state.codeImportSplitter,
        state.serialImportSplitter,
        onCodeImportSplitterChange,
        onSerialImportSplitterChange,
        tCatalog,
    ]);

    const canShowImportCsv = !!selectedDataSource
        && !isProductCodeAssignmentWithoutGroups
        && hasRequiredIndustry;

    return (
        <div className={'import-data-source-section'}>
            {state.step === ImportingSteps.selectDataSource && !selectedDataSource && (
                <DataSourceCatalog
                    options={dataSourcesOptions}
                    catalogContext={catalogContext}
                    onSelect={onDataSourceChanged}
                    loading={loading}
                    noCodeGroups={isProductCodeAssignmentWithoutGroups}
                />
            )}

            {state.step === ImportingSteps.selectDataSource && selectedDataSource && (
                <>
                    <SelectedDataSourceSummary
                        entry={selectedCatalogEntry}
                        showMultiselectTechnicalImportWarning={showMultiselectTechnicalImportWarning}
                        onChange={onChangeDataset}
                    />
                    {requiresIndustrySelection && (
                        <Box sx={{px: {xs: 2, md: 5}, mb: 2, width: '100%'}}>
                            <ImportIndustryController
                                industries={allIndustries}
                                value={state.importIndustryId}
                                onChange={onImportIndustryChange}
                                column
                                description={tCatalog('industryDescription')}
                            />
                        </Box>
                    )}
                    {!hasRequiredIndustry && requiresIndustrySelection && (
                        <Box sx={{px: {xs: 2, md: 5}, mb: 2}}>
                            <Alert severity="info" sx={{borderRadius: 1}}>
                                {tCatalog('industryRequired')}
                            </Alert>
                        </Box>
                    )}
                    {isProductCodeAssignmentWithoutGroups && !loading && (
                        <Box sx={{px: {xs: 2, md: 5}, mb: 2}}>
                            <Alert severity="warning" sx={{borderRadius: 1}}>
                                {tCatalog('alerts.noCodeGroups')}
                                <IconButton
                                    component={Link}
                                    to={routes.products.codeTypes}
                                    size={'small'}
                                    color={'inherit'}
                                    sx={{marginInlineStart: 0.5, verticalAlign: 'middle'}}
                                >
                                    <OpenInNew fontSize={'small'}/>
                                </IconButton>
                            </Alert>
                        </Box>
                    )}
                </>
            )}

            {state.step === ImportingSteps.mapColumns && (
                <div className={'px-5 w-100'}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        <strong>{t('mapColumns.selectedLabel')}</strong>{' '}
                        <Typography component={'span'} variant="body1" fontWeight={700} color="primary.main">
                            {selectedDataSource?.title}
                        </Typography>
                    </Typography>
                    <Typography variant="body2" color="textSecondary" marginBlockEnd={2}>
                        {transformingData ? t('mapColumns.transforming') : t('mapColumns.instruction')}
                    </Typography>
                </div>
            )}

            <div className={'mt-3'}/>
            <div className={'w-100 px-5'}>
                {(loading || transformingData) && (
                    <LoadingLottieAnimation width={'50%'}/>
                )}
                {!(loading || transformingData) && canShowImportCsv && (
                    <ImportCsvFile
                        formId={'import-csv-file-form'}
                        fields={fields}
                        sampleFileRowCount={sampleFileRowCount}
                        step={state.step === ImportingSteps.selectDataSource ? ImportCsvFileSteps.dropzone : ImportCsvFileSteps.columnMapper}
                        parsedColumns={state.parsedColumns}
                        parsedRows={state.parsedRows}
                        columnMapperInitialValues={state.columnMapping}
                        onDropzoneParsed={onDropzoneParsed}
                        onColumnMapperParsed={onColumnMapperParsed}
                        sampleFileTitle={`${selectedDataSource?.title} Import Template`}
                        offsetStartingHeight={64}
                        dropzoneNotes={dropzoneNotes}
                    />
                )}
            </div>
            <div className={'mb-4'}/>
            <div className={'mb-4'}/>
        </div>
    );
};

export default SelectImportDataSourceSection;
